import React from "react";
import { DateTime } from "luxon";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McButton } from "@maersk-global/mds-react-wrapper";

export default function StatisticsModal({
  isOpen,
  loading,
  selectedRow,
  statisticsData,
  incidentCounts,
  onClose,
}) {
  if (!isOpen) return null;

  const calculateDuration = (start, end) => {
    if (!start || !end) return "N/A";
    const startDate = DateTime.fromISO(start);
    const endDate = DateTime.fromISO(end);
    if (!startDate.isValid || !endDate.isValid) return "N/A";
    return `${Math.ceil(endDate.diff(startDate, "days").days)} days`;
  };

  const calculateProgress = (start, end) => {
    if (!start || !end) return 0;
    const startDate = DateTime.fromISO(start);
    const endDate = DateTime.fromISO(end);
    if (!startDate.isValid || !endDate.isValid) return 0;
    const totalDays = Math.ceil(endDate.diff(startDate, "days").days);
    const daysPassed = Math.ceil(DateTime.now().diff(startDate, "days").days);
    return totalDays > 0 ? Math.min(100, Math.round((daysPassed / totalDays) * 100)) : 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = DateTime.fromISO(dateStr);
    return date.isValid ? date.toFormat("d/M/yyyy") : "N/A";
  };

  const daysSince = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = DateTime.fromISO(dateStr);
    return date.isValid ? Math.ceil(DateTime.now().diff(date, "days").days) : "N/A";
  };

  const daysUntil = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = DateTime.fromISO(dateStr);
    return date.isValid ? Math.max(0, Math.ceil(date.diff(DateTime.now(), "days").days)) : "N/A";
  };

  return (
    <McModal
      heading="Statistics"
      dimension="large"
      padding="default"
      width="50%"
      open={isOpen}
      closed={onClose}
      closing={onClose}
      zindex={999}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <mc-loading-indicator></mc-loading-indicator>
        </div>
      ) : selectedRow ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Header */}
          <div style={{ display: "flex", gap: "16px", alignItems: "center", paddingBottom: "16px", borderBottom: "1px solid #e0e0e0" }}>
            <div>
              <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>
                App ID: <span style={{ fontWeight: "600", color: "#141414" }}>{selectedRow.app_id}</span>
              </div>
              <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>
                Incident: <span style={{ fontWeight: "600", color: "#141414" }}>{selectedRow.incident}</span>
              </div>
            </div>
            <div style={{ marginLeft: "auto" }}>
              <mc-tag appearance={selectedRow.status === "Red" ? "error" : "success"} fit="small">
                {selectedRow.status} Channel
              </mc-tag>
            </div>
          </div>

          {/* Change Implementation Duration */}
          <div style={{ padding: "20px", backgroundColor: "#e3f2fd", borderRadius: "8px" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "1rem", fontWeight: "600", color: "#141414" }}>
              Change Implementation Duration
            </h3>
            <div style={{ display: "flex", gap: "24px", marginBottom: "12px" }}>
              <div>
                <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Incident Date</div>
                <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#141414" }}>
                  {formatDate(selectedRow._originalData?.added_to_red_channel)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Final Review</div>
                <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#141414" }}>
                  {formatDate(selectedRow._originalData?.final_review)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Implementation Duration</div>
                <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#141414" }}>
                  {calculateDuration(selectedRow._originalData?.added_to_red_channel, selectedRow._originalData?.final_review)}
                </div>
              </div>
            </div>
            <div style={{ marginTop: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontSize: "0.875rem", color: "#666" }}>
                  Progress: {calculateProgress(selectedRow._originalData?.added_to_red_channel, selectedRow._originalData?.final_review)}%
                </span>
              </div>
              <div style={{ width: "100%", height: "8px", backgroundColor: "#b3e5fc", borderRadius: "4px", overflow: "hidden" }}>
                <div
                  style={{
                    width: `${calculateProgress(selectedRow._originalData?.added_to_red_channel, selectedRow._originalData?.final_review)}%`,
                    height: "100%",
                    backgroundColor: "#0277bd",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Success Rate */}
          <div style={{ padding: "20px", backgroundColor: "#e8f5e9", borderRadius: "8px" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "1rem", fontWeight: "600", color: "#141414" }}>
              Success Rate of Change Implementation
            </h3>
            {statisticsData ? (
              <>
                <div style={{ display: "flex", gap: "24px", marginBottom: "12px" }}>
                  <div>
                    <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Total Changes</div>
                    <div style={{ fontSize: "1.25rem", fontWeight: "600", color: "#141414" }}>{statisticsData.total || 0}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Implemented Successfully</div>
                    <div style={{ fontSize: "1.25rem", fontWeight: "600", color: "#2e7d32" }}>{statisticsData.successful || 0}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Success Rate</div>
                    <div style={{ fontSize: "1.25rem", fontWeight: "600", color: "#2e7d32" }}>
                      {statisticsData.successful_percent ? `${statisticsData.successful_percent.toFixed(1)}%` : "0%"}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: "16px" }}>
                  <div style={{ width: "100%", height: "8px", backgroundColor: "#c8e6c9", borderRadius: "4px", overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${statisticsData.successful_percent || 0}%`,
                        height: "100%",
                        backgroundColor: "#388e3c",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", color: "#666", padding: "1rem" }}>No statistics data available</div>
            )}
          </div>

          {/* Incident Count */}
          <div style={{ padding: "20px", backgroundColor: "#fff3e0", borderRadius: "8px", border: "1px solid #ffe0b2" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "1rem", fontWeight: "600", color: "#141414" }}>
              Incident Count for {selectedRow?.app_name || "Application"}
            </h3>
            {incidentCounts ? (
              <div style={{ display: "flex", gap: "24px", justifyContent: "space-between" }}>
                <div style={{ textAlign: "center", padding: "12px 24px", backgroundColor: "white", borderRadius: "8px", minWidth: "120px" }}>
                  <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "8px" }}>P1/P2 Incidents</div>
                  <mc-tag appearance="error" fit="small" style={{ marginBottom: "8px" }}>
                    {incidentCounts.p1p2_count || 0}
                  </mc-tag>
                  <div style={{ fontSize: "0.75rem", color: "#d32f2f", fontWeight: "600" }}>Critical & High Priority</div>
                </div>
                <div style={{ textAlign: "center", padding: "12px 24px", backgroundColor: "white", borderRadius: "8px", minWidth: "120px" }}>
                  <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "8px" }}>P3/P4 Incidents</div>
                  <mc-tag appearance="warning" fit="small" style={{ marginBottom: "8px" }}>
                    {incidentCounts.p3p4_count || 0}
                  </mc-tag>
                  <div style={{ fontSize: "0.75rem", color: "#f57c00", fontWeight: "600" }}>Medium & Low Priority</div>
                </div>
                <div style={{ textAlign: "center", padding: "12px 24px", backgroundColor: "white", borderRadius: "8px", minWidth: "120px" }}>
                  <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "8px" }}>Total Incidents</div>
                  <div style={{ fontSize: "2rem", fontWeight: "700", color: "#141414" }}>{incidentCounts.total || 0}</div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: "center", color: "#666", padding: "1rem" }}>No incident data available</div>
            )}
          </div>

          {/* Days Since/Until */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div style={{ padding: "16px", backgroundColor: "#e3f2fd", borderRadius: "8px" }}>
              <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "8px" }}>Days since incident</div>
              <div style={{ fontSize: "2rem", fontWeight: "600", color: "#0077b2" }}>
                {daysSince(selectedRow._originalData?.added_to_red_channel)}
              </div>
            </div>
            <div style={{ padding: "16px", backgroundColor: "#f3e5f5", borderRadius: "8px" }}>
              <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "8px" }}>Days until review</div>
              <div style={{ fontSize: "2rem", fontWeight: "600", color: "#6a1b9a" }}>
                {daysUntil(selectedRow._originalData?.final_review)}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>No data available</div>
      )}

      <McButton slot="primaryAction" appearance="primary" fit="medium" click={onClose}>
        Close
      </McButton>
    </McModal>
  );
}
